import { z } from "zod";
import { ApprovalRiskThresholdV1Schema, ExecutionModeV1Schema } from "./ai-policy";

/** Review Action Space identifiers. A policy flag alone does not provide an executor. */
export const AiActionIdV1Schema = z.enum([
  "pauseCampaign",
  "rapidTesting",
  "bidDayparting",
  "baseBid",
  "placementBid",
  "budgetDayparting",
  "reallocation",
  "budgetBoost",
  "harvest",
  "negateSource",
  "negation",
  "pauseAndSupplement",
]);
export type AiActionIdV1 = z.infer<typeof AiActionIdV1Schema>;

/** Excluded from this release, even if an older client submits a remembered choice. */
export const DEFERRED_AI_ACTIONS_V1 = [
  "rapidTesting",
  "bidDayparting",
  "budgetDayparting",
  "reallocation",
] as const satisfies readonly AiActionIdV1[];

/** Each action needs its own explicit authorization; omitted flags fail closed. */
export const AiActionFlagsV1Schema = z
  .object({
    pauseCampaign: z.boolean().default(false),
    rapidTesting: z.boolean().default(false),
    bidDayparting: z.boolean().default(false),
    baseBid: z.boolean().default(false),
    placementBid: z.boolean().default(false),
    budgetDayparting: z.boolean().default(false),
    reallocation: z.boolean().default(false),
    budgetBoost: z.boolean().default(false),
    harvest: z.boolean().default(false),
    negateSource: z.boolean().default(false),
    negation: z.boolean().default(false),
    pauseAndSupplement: z.boolean().default(false),
  })
  .strict();
export type AiActionFlagsV1 = z.infer<typeof AiActionFlagsV1Schema>;

export const AiActionBidBoundsV1Schema = z
  .object({
    min: z.number().finite().positive().max(1_000_000),
    max: z.number().finite().positive().max(1_000_000),
  })
  .strict()
  .refine((bounds) => bounds.min <= bounds.max, {
    path: ["max"],
    message: "max must be at least min",
  });
export type AiActionBidBoundsV1 = z.infer<typeof AiActionBidBoundsV1Schema>;

const ExclusionV1Schema = z.string().trim().min(1).max(255);
export const AiActionExclusionsV1Schema = z
  .array(ExclusionV1Schema)
  .max(200)
  .superRefine((values, context) => {
    const seen = new Set<string>();
    for (const [index, value] of values.entries()) {
      const normalized = value.toLocaleLowerCase("en-US");
      if (seen.has(normalized)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: "Duplicate exclusion",
        });
      }
      seen.add(normalized);
    }
  });

const AiActionPolicyFieldsV1Schema = z
  .object({
    version: z.literal(1),
    revision: z.number().int().positive(),
    shopId: z.string().uuid(),
    actions: AiActionFlagsV1Schema.default({}),
    bidBounds: AiActionBidBoundsV1Schema.optional(),
    budgetBoostPercentLimit: z.number().finite().positive().max(100).optional(),
    // Retained for older snapshots; enabling budgetBoost is the permission switch.
    budgetBoostExtraSpendConsent: z.boolean().default(false),
    budgetBoostDurationDays: z.number().int().min(1).max(7).default(1),
    harvestExclusions: AiActionExclusionsV1Schema.default([]),
    negativeExclusions: AiActionExclusionsV1Schema.default([]),
    approvalRiskThreshold: ApprovalRiskThresholdV1Schema.default("all"),
    dryRun: z.boolean().default(true),
  })
  .strict();

/** Source identity is part of the snapshot and cannot be exchanged across source kinds. */
export const AiActionPolicyV1Schema = z
  .discriminatedUnion("source", [
    AiActionPolicyFieldsV1Schema.extend({
      source: z.literal("smart_creation"),
      planId: z.string().uuid(),
      // A future enrollment preference is separate from the draft's dry-run action consent.
      requestedExecutionMode: ExecutionModeV1Schema.optional(),
    }).strict(),
    AiActionPolicyFieldsV1Schema.extend({
      source: z.literal("campaign"),
      campaignId: z.string().uuid(),
    }).strict(),
  ])
  .superRefine((policy, context) => {
    const { actions } = policy;
    // Harvest and supplementation create positive targets with an explicit bid.
    if (
      (actions.rapidTesting ||
        actions.bidDayparting ||
        actions.baseBid ||
        actions.placementBid ||
        actions.harvest ||
        actions.pauseAndSupplement) &&
      !policy.bidBounds
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bidBounds"],
        message: "An enabled bid action requires bid bounds",
      });
    }
    if (actions.budgetBoost && policy.budgetBoostPercentLimit === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["budgetBoostPercentLimit"],
        message: "Budget boost requires an explicit percent limit",
      });
    }
    // A plan may record future choices, but execution cannot be authorized before Campaign creation.
    if (policy.source === "smart_creation" && !policy.dryRun) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dryRun"],
        message: "Smart Creation policies must remain dry-run",
      });
    }
  });
export type AiActionPolicyV1 = z.infer<typeof AiActionPolicyV1Schema>;

const UnavailableReasonV1Schema = z.string().trim().min(1).max(500);

/** UI capability disclosure is separate from policy authorization and runtime gates. */
export const AiActionCapabilityV1Schema = z.discriminatedUnion("state", [
  z
    .object({
      action: AiActionIdV1Schema,
      state: z.literal("supported"),
      reason: z.null(),
    })
    .strict(),
  z
    .object({
      action: AiActionIdV1Schema,
      state: z.literal("configurable_after_creation"),
      reason: UnavailableReasonV1Schema,
    })
    .strict(),
  z
    .object({
      action: AiActionIdV1Schema,
      state: z.literal("unsupported"),
      reason: UnavailableReasonV1Schema,
    })
    .strict(),
]);
export type AiActionCapabilityV1 = z.infer<typeof AiActionCapabilityV1Schema>;
